import 'reflect-metadata'

import { Exclude, Expose, Transform, Type } from 'class-transformer'
// import { customAlphabet } from 'nanoid'
// import { customAlphabet } from 'nanoid-cjs'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { CardString, Hand, Hand as Solver, StandardHandTypeWithRoyalFlush } from 'pokersolver'
import { Subject, Subscriber } from 'rxjs'

import { PlayerEntity } from '../data/PlayerEntity'
import { PlayerEvent, PlayerEvents } from '../messaging/event/PlayerEvent'
import { Evented, VersionedEvent } from '../messaging/EventSourced'
import { DSColor, logger } from '../util'
import { ActionHint } from './ActionEvaluator'
import { Bet } from './Bet'
import { BotKind } from './BotKind'
import { LastAction } from './LastAction'
import { PlayerKind } from './PlayerKind'
import { PlayerStates } from './states/PlayerState'
import { PlayerActions } from './states/PlayerStateAction'
import { PlayerReducer } from './states/PlayerStateReducer'

import type { PlayerState } from './states/PlayerState.types'

const config = {
  DEFAULT_CHIP_COUNT: 10000
}

const customAlphabet = (alphabet: string, defaultSize = 21) => {
  return (size = defaultSize) => {
    let id = ''
    // A compact alternative for `for (var i = 0; i < step; i++)`.
    let i = size | 0
    while (i--) {
      // `| 0` is more compact and faster than `Math.floor()`.
      id += alphabet[(Math.random() * alphabet.length) | 0]
    }
    return id
  }
}

/**
 * nanoid for players follows Agora UID values 1 - 2^32
 */
const nanoid = customAlphabet('0123456789', 9)
const nanoPlayerId = () => {
  return `${1 + parseInt(nanoid(), 10)}`
}

export class Player extends Evented {
  /**
   * Available betting actions
   */
  @Expose()
  // @Transform(({ value }) => value.map((val: string) => ActionHint[val.toUpperCase() as keyof typeof ActionHint]))
  @Transform(({ value }) => {
    const mappedActions: ActionHint[] = value.map(
      (val: string) => ActionHint[val.toUpperCase() as keyof typeof ActionHint]
    )
    // if (type === TransformationType.CLASS_TO_PLAIN) {
    //   logger.trace('[Player.Transform.CLASS_TO_PLAIN]', mappedActions.length, mappedActions)
    // } else if (type === TransformationType.PLAIN_TO_CLASS) {
    //   logger.trace('[Player.Transform.PLAIN_TO_CLASS]', mappedActions.length, mappedActions)
    // }
    return mappedActions
  })
  actions: ActionHint[] = []
  /**
   * Total of all player bets in this hand. Defaults to 0.
   */
  bet = 0
  /**
   * Series of bet actions taken by the player, convenient
   * for checking player actions against the board
   */
  @Expose()
  @Type(() => Bet)
  bets: Bet[] = []
  /**
   * Players current chips at this table
   */
  chips: number
  /**
   * Ranked hand result from poker solver
   */
  @Exclude()
  rankedHand: Hand | undefined
  /**
   * Player's hole cards
   */
  @Type(() => String)
  public cards: CardString[] = []

  @Type(() => PlayerStates)
  public state: PlayerState = PlayerStates.Waiting

  rankedHandName: StandardHandTypeWithRoyalFlush | undefined
  rankedHandDescription: string | undefined

  doNoActionCount: number

  color: DSColor

  role: string

  missedBlinds = false

  observerId: number

  private seatId = 0

  playerOutTime = 0

  buyIn = true

  downGradeState = ''
  downGradeAmount = 0

  userTimedOut = false
  numberOfHandsPlayed = 0

  isCardsVisibleByTeacher = false
  isCardsVisibleByEveryone = false

  @Expose()
  @Type(() => LastAction)
  lastAction: LastAction | undefined

  // @Type(() => PlayerKind)
  kind: PlayerKind

  botKind: BotKind

  @Exclude()
  @Type(() => Subject)
  public event$ = new Subject<PlayerEvent | VersionedEvent>()

  constructor(
    public name: string,
    chips: number = config.DEFAULT_CHIP_COUNT,
    public userId?: string,
    public userRoles?: string[],
    // TODO this sould be not optional
    kind?: PlayerKind,
    subscriber?: Subscriber<PlayerEvent>,
    public id: string = nanoPlayerId(),
    rankedHandName?: StandardHandTypeWithRoyalFlush,
    rankedHandDescription?: string,
    botKind?: BotKind
  ) {
    super(id)
    this.observerId = 0
    this.chips = chips
    this.rankedHandName = this.cards.length > 0 ? rankedHandName : undefined
    this.rankedHandDescription = this.cards.length > 0 && rankedHandDescription ? rankedHandDescription : undefined
    this.doNoActionCount = 0
    this.color = 'Brand/Cream'
    this.role = ''
    this.downGradeState = ''
    this.downGradeAmount = 0
    this.userTimedOut = false
    this.lastAction = { action: '', amount: 0 }
    // TODO FRANCO: this default should be removed
    this.kind = kind || PlayerKind.HUMAN
    this.botKind = botKind || BotKind.BOT_PP
    this.numberOfHandsPlayed = 0

    if (subscriber) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.event$.subscribe(subscriber)
    }
    this.update(PlayerEvents.created(this))
  }

  get totalChips() {
    return this.chips + this.bets.reduce((acum, curr) => acum + curr.amount, 0)
  }

  /**
   * Removes chips from this players stack to "pay" to the pot.
   * @param bet amount to pay for a bet
   * @returns amount player bet
   */
  payBet(bet: Bet) {
    logger.trace(`[Player.payBet] ${bet.kind} ${bet.amount}`)
    let betAmount = bet.amount
    if (this.chips - betAmount <= 0) {
      //bet all in if amount greater than chips
      this.goAllIn()
      betAmount = this.chips
    }
    this.chips -= betAmount
    this.bet += betAmount
    logger.trace(`[Player.payBet] betAmount ${betAmount}`)
    this.bets.push(new Bet(betAmount, bet.kind))
    this.update(PlayerEvents.paidBet(this))
    return betAmount
  }
  /**
   * Give cards to the player during dealing
   *
   * @param cards Cards for the player
   */
  getCards(cards: CardString[]) {
    this.cards = this.cards.concat(cards)
    this.update(PlayerEvents.receivedCards(this))
  }
  /**
   * "Returns" cards and resets player for the next hand
   */
  returnCards() {
    this.resetPlayer()
    this.update(PlayerEvents.returnedCards(this))
  }
  /**
   * Get a player's cards
   * @returns Player's cards
   */
  showCards() {
    return this.cards
  }

  hideCards() {
    this.isCardsVisibleByEveryone = false
    this.isCardsVisibleByTeacher = false
    this.update(PlayerEvents.showCardsForTeacher(this))
  }

  showCardsForTeacher() {
    this.isCardsVisibleByTeacher = true
    this.update(PlayerEvents.showCardsForTeacher(this))
  }

  showCardsForEveryone() {
    this.isCardsVisibleByEveryone = true
    this.update(PlayerEvents.showCardsForTeacher(this))
  }

  /**
   * Whether the player has cards
   * @returns true if the player has cards
   */
  hasCards() {
    return this.cards.length > 0
  }

  setRankedHand(communityCards: CardString[]) {
    if (this.cards.length > 0) {
      try {
        const hand = Solver.solve([...this.cards, ...communityCards])
        hand.playerId = this.getId()
        if (hand.descr === 'Royal Flush') {
          hand.name = 'Royal Flush'
        }
        this.rankedHand = hand
        this.rankedHandName = hand.name
        this.rankedHandDescription = hand.descr
      } catch (err) {
        logger.error(err)
      }
    }
  }

  setSeatId(seatId: number) {
    this.seatId = seatId
  }

  setBuyIn() {
    this.buyIn = false
  }

  getSeatId() {
    return this.seatId
  }

  buyChips(chips: number) {
    if (chips > 0) {
      this.chips += chips
    }
    this.buyIn = true
    this.update(PlayerEvents.buyChips(this))
  }

  addChips(chips: number) {
    this.chips += chips
  }

  setActions(actions: ActionHint[]) {
    this.actions = actions
  }

  incrementNumberOfHandsPlayed() {
    this.numberOfHandsPlayed = this.numberOfHandsPlayed + 1
  }

  clearActions() {
    this.actions = []
  }

  beginPlay() {
    this.update(PlayerEvents.beganPlay(this))
  }

  fold() {
    this.foldPlayer()
    this.update(PlayerEvents.fold(this))
  }

  goAllIn() {
    this.update(PlayerEvents.allin(this))
  }

  out() {
    this.resetPlayer()
    this.update(PlayerEvents.out(this))
  }

  sitOut() {
    this.resetPlayer()
    this.update(PlayerEvents.sitout(this))
  }

  wait() {
    this.cards = []
    this.update(PlayerEvents.waiting(this))
  }

  foreverOut() {
    this.resetPlayer()
    this.update(PlayerEvents.foreverOut(this))
  }

  disconnect() {
    this.update(PlayerEvents.disconnected(this))
  }

  observer() {
    this.chips = 0
    this.update(PlayerEvents.observer(this))
  }

  onPokerPlayerHide_cards() {
    logger.trace('onPokerPlayerHide_cards')
  }

  onPokerPlayerShow_cards_for_teacher() {
    logger.trace('onPokerPlayerShow_cards_for_teacher')
  }

  onPokerPlayerShow_cards_for_everyone() {
    logger.trace('onPokerPlayerShow_cards_for_everyone')
  }

  onPokerPlayerCreated(event: PlayerEvent) {
    logger.trace('OnPokerPlayerCreated')
    this.setId(event.player.id)
    this.chips = event.player.chips
    this.name = event.player.name
    this.userId = event.player.userId
    this.userRoles = event.player.userRoles
  }

  onPokerPlayerBegan_play() {
    logger.trace('OnPokerPlayerBeganPlay')
    this.state = PlayerReducer(PlayerActions.BeginPlay, this.state)
  }

  onPokerPlayerFolded() {
    logger.trace('OnPokerPlayerFolded')
    // this.resetPlayer()
    this.foldPlayer()
    this.state = PlayerReducer(PlayerActions.Fold, this.state)
  }

  onPokerPlayerAllin() {
    logger.trace('OnPokerPlayerAllin')
    this.playerOutTime = this.currentCheckpoint()
    this.state = PlayerReducer(PlayerActions.GoAllIn, this.state)
  }

  onPokerPlayerOut() {
    logger.trace('onPokerPlayerOut')
    this.resetPlayer()
    this.state = PlayerReducer(PlayerActions.Out, this.state)
  }

  onPokerPlayerSitout() {
    logger.trace('OnPokerPlayerSitout')
    this.resetPlayer()
    this.state = PlayerReducer(PlayerActions.SitOut, this.state)
  }

  onPokerPlayerWaiting() {
    logger.trace('OnPokerPlayerWaiting')
    this.state = PlayerReducer(PlayerActions.Wait, this.state)
  }

  onPokerPlayerForeverout() {
    logger.trace('onPokerPlayerForeverout')
    this.state = PlayerReducer(PlayerActions.OutForever, this.state)
  }

  onPokerPlayerDisconnected() {
    logger.trace('OnPokerPlayerDisconnected')
    this.state = PlayerReducer(PlayerActions.Disconnect, this.state)
  }

  onPokerPlayerObserver() {
    logger.trace('onPokerPlayerObserver')
    this.state = PlayerReducer(PlayerActions.Observ, this.state)
  }

  onPokerPlayerPaid_bet(event: PlayerEvent) {
    logger.trace('OnPokerPlayerPaid_bet')
    this.chips = event.player.chips
  }

  onPokerPlayerReceived_cards(event: PlayerEvent) {
    logger.trace('OnPokerPlayerReceived_cards')
    this.cards = event.player.cards
  }

  onPokerPlayerReturned_cards() {
    logger.trace('OnPokerPlayerReturned_cards')
    this.resetPlayer()
  }

  onPokerPlayerBuyin(event: PlayerEvent) {
    logger.trace('OnPokerPlayerBuyin')
    this.chips = event.player.chips
  }

  onPokerPlayerDonoaction() {
    logger.trace('onPokerPlayerDonoaction')
  }

  isAllIn() {
    return PlayerStates.isAllIn(this.state)
  }

  isFolded() {
    return PlayerStates.isFolded(this.state)
  }

  isInPlay() {
    return PlayerStates.isInPlay(this.state)
  }

  isOut() {
    return PlayerStates.isOut(this.state)
  }

  isSittingOut() {
    return PlayerStates.isSitOut(this.state)
  }

  isWaiting() {
    return PlayerStates.isWaiting(this.state)
  }

  isForeverOut() {
    return PlayerStates.isForeverOut(this.state)
  }

  isDisconnected() {
    return PlayerStates.isDisconnected(this.state)
  }

  isObserver() {
    return PlayerStates.isObserver(this.state)
  }

  isTeachingObserver() {
    return this.isObserver() && this.observerId === 1
  }

  canCheck(playerActions?: ActionHint[]) {
    if (playerActions?.length) {
      return playerActions.some((action) => action === ActionHint.CHECK)
    }
    return this.actions.some((action) => action === ActionHint.CHECK)
  }

  canFold() {
    return this.actions.some((action) => action === ActionHint.FOLD)
  }

  doNoAction() {
    this.update(PlayerEvents.doNoAction(this))
  }

  update(event: PlayerEvent | VersionedEvent) {
    const isPlayerOut = this.isForeverOut() || this.isSittingOut() || this.isOut()
    const shouldUpdateCheckpoint = !isPlayerOut
    this.updateState(event, shouldUpdateCheckpoint)

    this.event$.next(event)
  }

  internalState(): string {
    return this.state.tag
  }

  getId(): string {
    return super.getId()
  }
  getVersion(): number {
    return super.getVersion()
  }
  getEvents(): VersionedEvent[] {
    return super.getEvents()
  }

  resetBets() {
    logger.trace('[Player.resetBets]')
    this.bet = 0
    this.bets = []
  }

  resetLastAction() {
    logger.trace('[Player.resetLastAction]')
    this.lastAction = { action: '', amount: 0 }
  }

  resetDoNoActionCount() {
    this.doNoActionCount = 0
  }

  setUserTimedOut(value: boolean) {
    this.userTimedOut = value
  }

  resetPlayer(clearCards = true) {
    logger.trace('resetPlayer')
    if (clearCards) {
      this.cards = []
    }
    this.rankedHand = undefined
    this.rankedHandName = undefined
    this.rankedHandDescription = undefined
    this.actions = []
    // this.doNoActionCount = 0
    this.downGradeState = ''
    this.downGradeAmount = 0
    this.userTimedOut = false
  }

  foldPlayer() {
    logger.trace('foldPlayer')
    this.rankedHand = undefined
    this.actions = []
  }

  static fromPlayerEntity(entity: PlayerEntity) {
    return new Player(
      entity.name,
      entity.chips,
      entity.userId,
      entity.userRoles,
      entity.kind,
      undefined,
      entity.id,
      undefined,
      undefined,
      entity.botKind
    )
  }
}
